<div class="space-y-8" [formGroup]="form()">
  <section class="space-y-4">
    <div>
      <h3 class="text-sm font-bold text-slate-900">
        Benchmark Source
        <span class="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">CALCULATED</span>
      </h3>
      <p class="mt-0.5 text-sm text-slate-500">Choose how the CI benchmark should be calculated for this property.</p>
    </div>

    <div class="grid gap-4 sm:grid-cols-3">
      @for (source of sources; track source.value) {
        <label
          class="relative flex cursor-pointer flex-col gap-2 rounded-xl border p-5 transition-colors"
          [class]="form().controls.source.value === source.value ? 'border-indigo-400 bg-indigo-50/60 ring-1 ring-indigo-400' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'"
        >
          <input type="radio" formControlName="source" [value]="source.value" class="sr-only" />
          <span class="flex items-center gap-2">
            <span class="flex size-5 items-center justify-center rounded-full border-2" [class]="form().controls.source.value === source.value ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'">
              @if (form().controls.source.value === source.value) {
                <span class="size-2 rounded-full bg-white"></span>
              }
            </span>
            <span class="text-sm font-bold text-slate-900">{{ source.label }}</span>
          </span>
          @if (source.recommended) {
            <span class="w-fit rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">RECOMMENDED</span>
          }
          <p class="text-sm leading-relaxed text-slate-500">{{ source.description }}</p>
        </label>
      }
    </div>
  </section>

  <div class="h-px bg-slate-200"></div>

  @if (isTransactionsSource()) {
    <section class="space-y-4" formArrayName="transactions">
      <div>
        <h3 class="text-sm font-bold text-slate-900">Property Transaction History</h3>
        <p class="mt-0.5 text-sm text-slate-500">Enter available transaction evidence for this property.</p>
      </div>
      @for (row of form().controls.transactions.controls; track row; let i = $index) {
        <div class="grid items-end gap-4 rounded-xl border border-slate-200 p-5 sm:grid-cols-[auto_1fr_1fr_1fr_auto]" [formGroupName]="i">
          <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Transaction {{ i + 1 }}</span>
          <ui-input [control]="row.controls.psf" label="Transaction PSF" type="number" prefix="₹" suffix="/sq.ft" />
          <ui-input [control]="row.controls.source" label="Source" placeholder="Zapkey, Internal Data…" />
          <ui-input [control]="row.controls.date" label="Transaction Date" type="date" />
          <button type="button" (click)="removeTransaction(i)"
            [disabled]="form().controls.transactions.length <= 1"
            [attr.aria-label]="'Delete benchmark row ' + (i + 1)"
            class="h-11 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-red-600 transition-colors hover:border-red-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40">
            Delete
          </button>
        </div>
      }
      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="text-xs text-slate-500">At least one benchmark with a PSF and date is required. Add up to {{ maxTransactions }} rows.</p>
        <button type="button" (click)="addTransaction()"
          [disabled]="form().controls.transactions.length >= maxTransactions"
          class="h-10 rounded-lg border border-indigo-200 bg-white px-4 text-sm font-semibold text-indigo-600 transition-colors hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40">
          + Add benchmark ({{ form().controls.transactions.length }}/{{ maxTransactions }})
        </button>
      </div>
    </section>

    <div class="grid gap-4 sm:grid-cols-2">
      <ui-calculated-box label="Last 5 Transaction Average" [value]="last5Average()" [derivation]="usableCount() + ' transaction(s) available'" />
      <ui-calculated-box label="Latest Transaction" [value]="latestTransaction()" [derivation]="latestTransactionDate()" />
    </div>

    <div class="grid gap-6 sm:grid-cols-2">
      <ui-input [control]="form().controls.latestPsfOverride" label="Latest Transaction PSF (override)" type="number" prefix="₹" suffix="/sq.ft" hint="Correct the auto-detected latest transaction if required." />
      <ui-input [control]="form().controls.latestDateOverride" label="Latest Transaction Date (override)" type="date" />
    </div>
  } @else if (form().controls.source.value === 'similar-listings') {
    <section class="space-y-4" formArrayName="transactions">
      <div>
        <h3 class="text-sm font-bold text-slate-900">Similar Listed Properties</h3>
        <p class="mt-0.5 text-sm text-slate-500">Select one similar property per row. The base benchmark is the average of their CI benchmarks.</p>
        <p class="mt-2 text-xs text-slate-500">Showing properties from: <strong class="text-slate-700">{{ microMarket() || 'the selected micro market' }}</strong></p>
      </div>

      @for (row of form().controls.transactions.controls; track row; let i = $index) {
        <div class="space-y-4 rounded-xl border border-slate-200 bg-white p-5" [formGroupName]="i">
          <div class="flex items-center justify-between gap-3">
            <h4 class="text-xs font-bold uppercase tracking-wide text-slate-500">Benchmark {{ i + 1 }}</h4>
            <button type="button" (click)="removeTransaction(i)"
              [disabled]="form().controls.transactions.length <= 1"
              [attr.aria-label]="'Delete benchmark row ' + (i + 1)"
              class="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40">Delete</button>
          </div>

          <div class="grid items-start gap-4 sm:grid-cols-2 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <ci-property-search
              class="block min-w-0 [&_ul]:grid-cols-1 [&_ul]:gap-2"
              [selectedId]="comparables().get(row)?.property?.id ?? null"
              [microMarket]="microMarket()"
              (picked)="selectComparable(row, $event)"
            />
            <ui-input class="min-w-0" [control]="row.controls.psf" label="CI Benchmark PSF" type="number" prefix="₹" suffix="/sq.ft" [readonly]="true" hint="Filled from the selected property's CI benchmark." />
            <div class="min-w-0 space-y-2">
              <ui-input [control]="row.controls.source" label="Source property" [readonly]="true" />
              @if (row.controls.source.value) {
                <button type="button" (click)="clearComparable(row)" class="text-xs font-semibold text-indigo-700 hover:underline">Remove selection</button>
              }
            </div>
            <ui-input class="min-w-0" [control]="row.controls.date" label="Benchmark Date" type="date" hint="CI update date when available; otherwise the selection date." />
          </div>
          @if (comparables().get(row)?.loading) {
            <p class="text-xs text-slate-500" role="status">Loading property benchmark…</p>
          }
          @if (comparables().get(row)?.error; as error) {
            <p class="text-xs text-red-600" role="alert">{{ error }}</p>
          }

        </div>
      }
      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="text-xs text-slate-500">At least one benchmark is required. Add up to {{ maxTransactions }} similar properties.</p>
        <button type="button" (click)="addTransaction()"
          [disabled]="form().controls.transactions.length >= maxTransactions"
          class="h-10 rounded-lg border border-indigo-200 bg-white px-4 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40">
          + Add benchmark ({{ form().controls.transactions.length }}/{{ maxTransactions }})
        </button>
      </div>
    </section>
  } @else {
    <section class="space-y-4" formArrayName="transactions">
      <div>
        <h3 class="text-sm font-bold text-slate-900">Anchor Properties</h3>
        <p class="mt-0.5 text-sm text-slate-500">Select or add one external anchor property per row. The base benchmark is the average of their benchmark values.</p>
      </div>
      @for (row of form().controls.transactions.controls; track row; let i = $index) {
        <div class="space-y-4 rounded-xl border border-slate-200 bg-white p-5" [formGroupName]="i">
          <div class="flex items-center justify-between gap-3">
            <h4 class="text-xs font-bold uppercase tracking-wide text-slate-500">Benchmark {{ i + 1 }}</h4>
            <button type="button" (click)="removeTransaction(i)" [disabled]="form().controls.transactions.length <= 1"
              [attr.aria-label]="'Delete benchmark row ' + (i + 1)"
              class="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40">Delete</button>
          </div>
          <div class="grid items-start gap-4 sm:grid-cols-2 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <div class="min-w-0 space-y-2">
              <div class="relative" (keydown.escape)="anchorState(row).showResults = false" (focusout)="closeAnchorSearch(row, $event)">
              <ui-input [control]="anchorState(row).search" (input)="anchorState(row).showResults = true" label="Search anchor property" placeholder="Search or add an anchor property…" />
                @if (anchorState(row).showResults && anchorState(row).search.value.trim()) {
                <div class="absolute inset-x-0 top-full z-40 mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl">
                  @for (property of filteredAnchorProperties(row); track property.id) {
                    <button type="button" (click)="selectAnchor(row, property)"
                      [class.bg-indigo-50]="anchorState(row).selectedId === property.id"
                      class="flex w-full flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-left hover:bg-indigo-50">
                      <span class="text-sm font-semibold text-slate-900">{{ property.name }}</span>
                      <span class="text-xs font-semibold text-slate-600">{{ psf(property.psf) }}</span>
                    </button>
                  } @empty {
                    <p class="p-3 text-xs text-slate-500">No anchor properties match this search.</p>
                  }
                </div>
                }
              </div>
                <button type="button" (click)="anchorState(row).showForm = true; anchorState(row).showResults = false" class="w-full px-3 py-2 text-left text-sm font-semibold text-indigo-600 hover:bg-indigo-50">+ Add New Anchor Property</button>
            </div>
            <ui-input class="min-w-0" [control]="row.controls.psf" label="Anchor Benchmark PSF" type="number" prefix="₹" suffix="/sq.ft" hint="Filled from the selected anchor; editable if needed." />
            <div class="min-w-0 space-y-2">
              <ui-input [control]="row.controls.source" label="Source property" [readonly]="true" />
              @if (row.controls.source.value) {
                <button type="button" (click)="clearAnchor(row)" class="text-xs font-semibold text-indigo-700 hover:underline">Remove selection</button>
              }
            </div>
            <ui-input class="min-w-0" [control]="row.controls.date" label="Benchmark Date" type="date" hint="Defaults to the selection date; update if needed." />
          </div>
          @if (anchorState(row).showForm) {
            <div class="space-y-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-4">
              <h4 class="text-sm font-bold text-slate-900">Add Anchor Property</h4>
              <div class="grid gap-4 sm:grid-cols-2">
                <ui-input [control]="anchorState(row).name" label="Property Name" placeholder="External property name" />
                <ui-input [control]="anchorState(row).psf" label="Benchmark PSF" type="number" prefix="₹" suffix="/sq.ft" />
              </div>
              <div class="flex gap-3">
                <button type="button" (click)="saveAnchor(row)"
                  [disabled]="!anchorState(row).name.value.trim() || !(anchorState(row).psf.value! > 0)"
                  class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40">Save Anchor Property</button>
                <button type="button" (click)="anchorState(row).showForm = false" class="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700">Cancel</button>
              </div>
            </div>
          }
        </div>
      }
      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="text-xs text-slate-500">At least one benchmark with a PSF and date is required. Add up to {{ maxTransactions }} anchor properties.</p>
        <button type="button" (click)="addTransaction()" [disabled]="form().controls.transactions.length >= maxTransactions"
          class="h-10 rounded-lg border border-indigo-200 bg-white px-4 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40">
          + Add benchmark ({{ form().controls.transactions.length }}/{{ maxTransactions }})
        </button>
      </div>
    </section>
  }

  <div class="rounded-xl border border-indigo-200 bg-indigo-50/70 p-5">
    <p class="text-xs font-semibold uppercase tracking-wide text-indigo-600">Base Benchmark</p>
    <p class="mt-1 text-3xl font-bold tabular-nums text-indigo-900">{{ baseBenchmark() }}</p>
    <div class="mt-3 flex flex-wrap gap-x-10 gap-y-1 text-xs text-indigo-700">
      <span><span class="text-indigo-500">Benchmark Source</span> · {{ sourceLabel() }}</span>
      <span><span class="text-indigo-500">Calculation Logic</span> · {{ calculationLogic() }}</span>
    </div>
    <p class="mt-2 text-xs text-indigo-600">This is the primary benchmark used by CI. All downstream CI outputs use this benchmark.</p>
  </div>
</div>
